/**
 * Canonical error-reason values used by the API domain layer.
 * Keep in sync with AIRDROP_FARMING_ERROR_REASON_VALUES in index.ts.
 */
export const AIRDROP_FARMING_ERROR_REASONS = [
  // Shared
  "insufficient_balance",
  "agent_wallet_revoked",
  "exchange_error",
  "rate_limit",
  "rate_limit_topup_failed",
  "rate_limit_topup_cap_exceeded",
  "stop_retries_exhausted",
  "margin_constrained",
  "stop_loss_placement_failed",
  "ws_connection_error",
  "open_failed",
  "close_failed",
  "user_cancelled",
] as const;

export type AirdropFarmingErrorReason = (typeof AIRDROP_FARMING_ERROR_REASONS)[number];

/**
 * Canonical stop-reason values used by the grid bot domain.
 */
export const GRID_BOT_STOP_REASONS = [
  "user",
  "max_loss",
  "volume_target",
  "error",
  "admin",
  "exchange_maintenance",
] as const;

export type GridBotStopReason = (typeof GRID_BOT_STOP_REASONS)[number];

/**
 * User-facing labels for bot error reasons (when stopReason = "error").
 */
export const AIRDROP_FARMING_ERROR_LABELS: Record<AirdropFarmingErrorReason, string> = {
  insufficient_balance: "Insufficient balance",
  agent_wallet_revoked: "Wallet revoked — please reconnect",
  rate_limit: "Rate limit reached",
  rate_limit_topup_failed: "Rate limit top-up failed",
  rate_limit_topup_cap_exceeded: "Rate limit top-up cap exceeded",
  exchange_error: "Exchange error",
  stop_retries_exhausted: "Cleanup failed",
  margin_constrained: "Not enough margin",
  stop_loss_placement_failed: "Stop-loss placement failed",
  ws_connection_error: "WebSocket connection lost",
  open_failed: "Failed to open position",
  close_failed: "Failed to close position",
  user_cancelled: "Cancelled by user",
};

/**
 * User-facing labels for bot stop reasons (why the bot stopped).
 */
export const GRID_BOT_STOP_LABELS: Record<GridBotStopReason, string> = {
  user: "Stopped by user",
  max_loss: "Max loss threshold reached",
  volume_target: "Volume target reached",
  error: "Bot encountered an error",
  admin: "Stopped by an administrator",
  exchange_maintenance: "Bot paused to avoid losses during scheduled Exchange maintenance",
};

/**
 * Canonical stop-reason values used by the delta-neutral bot domain.
 * Kept separate from the grid-bot reasons because the terminal statuses
 * and the `leg_closed` case are DN-specific.
 */
export const DELTA_NEUTRAL_STOP_REASONS = [
  "user",
  "max_loss",
  "error",
  "leg_closed",
  "admin",
  "exchange_maintenance",
] as const;

export type DeltaNeutralStopReason = (typeof DELTA_NEUTRAL_STOP_REASONS)[number];

export const DELTA_NEUTRAL_STOP_LABELS: Record<DeltaNeutralStopReason, string> = {
  user: "Stopped by user",
  max_loss: "Max loss threshold reached",
  error: "Bot encountered an error",
  leg_closed: "Leg closed",
  admin: "Stopped by an administrator",
  exchange_maintenance: "Bot paused to avoid losses during scheduled Exchange maintenance",
};

/**
 * Resolves a human-readable title from errorReason and/or stopReason.
 * Prefers errorReason when available, falls back to stopReason.
 */
export function getBotStatusTitle(
  stopReason: string | null | undefined,
  errorReason: string | null | undefined,
): string {
  if (errorReason && errorReason in AIRDROP_FARMING_ERROR_LABELS) {
    return AIRDROP_FARMING_ERROR_LABELS[errorReason as AirdropFarmingErrorReason];
  }
  if (stopReason && stopReason in GRID_BOT_STOP_LABELS) {
    return GRID_BOT_STOP_LABELS[stopReason as GridBotStopReason];
  }
  return "Bot encountered an error";
}
